<template>
  <div class="aplikasi-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Pengaturan Aplikasi</h1>
      <p class="text-gray-600">Kelola tampilan dan konten aplikasi</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Gambar Halaman Depan Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-1">
            Gambar Halaman Depan
          </h2>
          <p class="text-sm text-gray-600">
            Kelola gambar yang ditampilkan di halaman depan aplikasi
          </p>
        </div>

        <!-- Current Image Preview -->
        <div v-if="currentImage" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">
            Gambar Saat Ini
          </label>
          <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div class="flex flex-col md:flex-row gap-4 items-start">
              <div class="w-full md:w-64 h-40 bg-white border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center p-2">
                <img 
                  v-if="currentImage.imageUrl" 
                  :src="getImageUrl(currentImage.imageUrl)" 
                  :alt="currentImage.label"
                  class="max-w-full max-h-full object-contain"
                  @error="handleImageError"
                >
                <div v-else class="text-gray-400 text-center">
                  <i class="el-icon-picture-outline text-4xl"></i>
                  <p class="text-sm mt-2">Tidak ada gambar</p>
                </div>
              </div>
              <div class="flex-1">
                <div class="space-y-2">
                  <div>
                    <span class="text-sm font-medium text-gray-600">Label:</span>
                    <span class="text-sm text-gray-800 ml-2">{{ currentImage.label }}</span>
                  </div>
                  <div>
                    <span class="text-sm font-medium text-gray-600">Status:</span>
                    <span 
                      :class="currentImage.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'" 
                      class="ml-2 px-2 py-1 rounded text-xs font-medium"
                    >
                      {{ currentImage.enabled ? 'Aktif' : 'Nonaktif' }}
                    </span>
                  </div>
                  <div v-if="currentImage.altText">
                    <span class="text-sm font-medium text-gray-600">Alt Text:</span>
                    <span class="text-sm text-gray-800 ml-2">{{ currentImage.altText }}</span>
                  </div>
                  <div v-if="currentImage.lastUpdate">
                    <span class="text-sm font-medium text-gray-600">Terakhir Diupdate:</span>
                    <span class="text-sm text-gray-800 ml-2">{{ formatDate(currentImage.lastUpdate) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload New Image Form -->
        <div class="border-t pt-6">
          <h3 class="text-md font-semibold text-gray-800 mb-4">
            {{ currentImage ? 'Ganti Gambar' : 'Upload Gambar Baru' }}
          </h3>
          
          <form @submit.prevent="handleUploadImage" class="space-y-4">
            <!-- File Upload -->
            <div>
              <label for="imageFile" class="block text-sm font-medium text-gray-700 mb-2">
                {{ currentImage ? 'Pilih Gambar Baru' : 'Pilih Gambar' }} <span v-if="!currentImage" class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-4">
                <input 
                  id="imageFile"
                  ref="fileInput"
                  type="file" 
                  @change="handleFileChange"
                  accept="image/*"
                  class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  :disabled="uploading"
                  :required="!currentImage"
                >
              </div>
              <p class="mt-1 text-xs text-gray-500">
                Format: JPG, PNG, GIF, WebP, SVG, BMP, TIFF. Maksimal 10MB
              </p>
              
              <!-- Image Preview -->
              <div v-if="previewUrl" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <div class="w-64 h-40 bg-white border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center p-2">
                  <img 
                    :src="previewUrl" 
                    alt="Preview"
                    class="max-w-full max-h-full object-contain"
                  >
                </div>
              </div>
            </div>

            <!-- Label Input -->
            <div>
              <label for="imageLabel" class="block text-sm font-medium text-gray-700 mb-2">
                Label Gambar <span class="text-red-500">*</span>
              </label>
              <input 
                id="imageLabel"
                v-model="uploadForm.label" 
                type="text" 
                placeholder="Masukkan label gambar"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :disabled="uploading"
                required
              >
            </div>

            <!-- Alt Text Input -->
            <div>
              <label for="imageAltText" class="block text-sm font-medium text-gray-700 mb-2">
                Alt Text (opsional)
              </label>
              <input 
                id="imageAltText"
                v-model="uploadForm.altText" 
                type="text" 
                placeholder="Teks alternatif untuk accessibility"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :disabled="uploading"
              >
            </div>

            <!-- Submit Button -->
            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="resetForm"
                class="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                :disabled="uploading"
              >
                Batal
              </button>
              <button
                type="submit"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                :disabled="uploading || (!currentImage && !uploadForm.file)"
              >
                <span v-if="uploading">
                  <i class="el-icon-loading mr-2"></i>
                  Mengupload...
                </span>
                <span v-else>
                  <i class="el-icon-upload2 mr-2"></i>
                  {{ currentImage ? 'Ganti Gambar' : 'Upload Gambar' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'AplikasiView',
  data() {
    return {
      previewUrl: null,
      uploadForm: {
        file: null,
        label: '',
        altText: ''
      }
    };
  },
  computed: {
    ...mapState('dynamicImage', ['currentImage']),
    ...mapGetters('dynamicImage', ['isLoading', 'isUploading']),
    loading() {
      return this.isLoading;
    },
    uploading() {
      return this.isUploading;
    }
  },
  mounted() {
    this.loadCurrentImage();
  },
  watch: {
    currentImage: {
      handler(newImage) {
        if (newImage && !this.uploadForm.label) {
          // Auto-fill form with current image data
          this.uploadForm.label = newImage.label;
          this.uploadForm.altText = newImage.altText || '';
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    // Clean up preview URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    // Clear store state
    this.clearCurrentImage();
  },
  methods: {
    ...mapActions('dynamicImage', [
      'fetchImageById',
      'uploadImage',
      'updateImage',
      'getImageUrl',
      'clearCurrentImage'
    ]),

    async loadCurrentImage() {
      try {
        // Load dynamic image with ID 1
        await this.fetchImageById(1);
        // Auto-fill form with current image data if exists
        if (this.currentImage) {
          this.uploadForm.label = this.currentImage.label || '';
          this.uploadForm.altText = this.currentImage.altText || '';
        }
      } catch (error) {
        console.error('Error loading current image:', error);
        if (error.response && error.response.status !== 404) {
          this.$message.error('Gagal memuat data gambar saat ini');
        }
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('Ukuran file terlalu besar. Maksimal 10MB');
        this.$refs.fileInput.value = '';
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$message.error('File harus berupa gambar');
        this.$refs.fileInput.value = '';
        return;
      }

      this.uploadForm.file = file;

      // Create preview URL
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = URL.createObjectURL(file);
    },

    async handleUploadImage() {
      // Validate: new image must have file, update can have file optional
      if (!this.currentImage && !this.uploadForm.file) {
        this.$message.warning('Pilih gambar terlebih dahulu');
        return;
      }

      try {
        const payload = {
          file: this.uploadForm.file,
          label: this.uploadForm.label,
          categoryId: 1, // Default category ID
          displayOrder: 1,
          altText: this.uploadForm.altText
        };

        // If image exists, update it. Otherwise, create new
        if (this.currentImage && this.currentImage.id) {
          payload.id = this.currentImage.id;
          await this.updateImage(payload);
        } else {
          await this.uploadImage(payload);
        }

        this.$message.success('Gambar berhasil diupload');
        this.resetForm();
        // Reload current image to get updated data
        await this.loadCurrentImage();
      } catch (error) {
        console.error('Error uploading image:', error);
        const errorMessage = error.response?.data?.message || 'Gagal mengupload gambar';
        this.$message.error(errorMessage);
      }
    },

    resetForm() {
      // Only reset file and preview, keep label and altText from current image
      this.uploadForm.file = null;
      
      if (this.currentImage) {
        this.uploadForm.label = this.currentImage.label || '';
        this.uploadForm.altText = this.currentImage.altText || '';
      } else {
        this.uploadForm.label = '';
        this.uploadForm.altText = '';
      }
      
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    getImageUrl(path) {
      if (!path) return '';
      // If path is already a full URL, return it
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      // Construct URL with API endpoint
      const host = process.env.VUE_APP_API_HOST || 'http://localhost:8020';
      return `${host}/api/tool/dynamic-image/view/${this.currentImage.id}`;
    },

    handleImageError(event) {
      console.error('Error loading image');
      event.target.src = ''; // Clear broken image
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
/* Custom styles if needed */
.aplikasi-container {
  max-width: 1200px;
}

/* Smooth transitions */
.transition-colors {
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* File input styling enhancement */
input[type="file"]::file-selector-button {
  cursor: pointer;
}
</style>
